import { Injectable } from '@angular/core';
import { PermissionsService } from 'app/core/permissions.service';
import { Widget } from 'app/viewmodels/dashboard/widget';
import { Observable, Subject } from 'rxjs';
import { BackendPaginationService } from '../../core/backend-pagination.service';
import { BackendService } from '../../core/backend.service';
import { ApiResult } from '../../core/messages/api-result';
import { HierarchyDashboardDrilldownService } from '../../shared/dashboard-drilldown/dashboard-drilldown.service';
import { DashboardData } from '../../viewmodels/dashboard-data';
import { DashboardWidget } from '../../viewmodels/dashboard/dashboard-widget';
import { WidgetDataType } from './widgets/widget-data-type';
import { WidgetSettings } from './widgets/widget-settings';

declare let $: any;

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  public brandPrimary = '#20a8d8';
  public brandSuccess = '#4dbd74';
  public brandInfo = '#63c2de';
  public brandWarning = '#f8cb00';
  public brandDanger = '#f86c6b';
  public grayDark = '#154b7c';
  public grayLighter = '#95adc3';

  public dataInitialised = new Subject<DashboardData>();
  public errors = new Subject<ApiResult>();
  public widgetsUpdated = new Subject<DashboardWidget[]>();
  public dashboardWidgets = new Array<DashboardWidget>();
  public dashboardId: string;

  public highRiskItemFilters = new Map();
  public itemsPerAssetCountFilters = new Map();

  private pieRiskMatrix = new Array<string>();

  private highRiskItemRiskBandFilterValues = new Array<string>();

  constructor(
    private backendService: BackendService,
    private backendPaginationService: BackendPaginationService,
    private hierarchyDrilldownService: HierarchyDashboardDrilldownService,
    private permissionService: PermissionsService,
  ) { }

  public initialiseDashboardData() {
    this.backendService.get<DashboardData>(DashboardData).subscribe(
      data => {
        data.dashboardWidgets = data.dashboardWidgets.filter(e => e.widget && this.checkUserHasPermissionsForWidget(e.widget.widgetDataType));
        this.dataInitialised.next(data);
        this.dashboardWidgets = data.dashboardWidgets;

        data.riskMatrixBands.forEach(band => {
          this.pieRiskMatrix.push(data.defaultRiskMatrix);
        });

        this.highRiskItemRiskBandFilterValues = data.highRiskItemRiskBandFilterValues;
        this.dashboardWidgets.forEach(w => this.getWidgetData(w));
      },
      (errorResult: ApiResult) => {
        if (errorResult) {
          this.errors.next(errorResult);
        }
      },
    );
  }

  public initialiseWidgetFilters(dashboardWidget: DashboardWidget) {
    switch (dashboardWidget.widget.widgetDataType) {
      case WidgetDataType.HighRiskItems:
        if (!$.isEmptyObject(dashboardWidget.widget.data)) {
          this.highRiskItemFilters.set(this.hierarchyDrilldownService.ModelNameFilter.displayName, dashboardWidget.widget.data.Labels);
          this.highRiskItemFilters.set(this.hierarchyDrilldownService.RiskBandFilter.displayName, this.highRiskItemRiskBandFilterValues);
        }
        break;
      case WidgetDataType.AssetCount:
        if (!$.isEmptyObject(dashboardWidget.widget.data)) {
          this.itemsPerAssetCountFilters.set(this.hierarchyDrilldownService.TemplateFilter.displayName, dashboardWidget.widget.data.Labels);
        }
        break;
      default:
        break;
    }
  }

  public getWidgetData(widget: DashboardWidget): void {
    this.backendService.getById<DashboardWidget>(DashboardWidget, widget.dashboardWidgetId).subscribe(data => {
      if (data) {
        widget.widget.data = data.widget.data;
        widget.widget.widgetParameters = data.widget.widgetParameters;
        this.initialiseWidgetFilters(widget);
      }
    });
  }

  public updateWidget(widget: Widget, updateWidgetParams: boolean = true) {
    return this.backendService
      .update<Widget>(Widget, `${widget.widgetId}/${updateWidgetParams}`, this.getWidgetForApiUpdate(widget), false)
      .subscribe((updatedWidget: Widget) => {
        if (updatedWidget) {
          const widgetPosition = this.dashboardWidgets.findIndex(dw => dw.widgetId === updatedWidget.widgetId);
          let newDashboardWidget = new DashboardWidget();
          newDashboardWidget = Object.assign(newDashboardWidget, this.dashboardWidgets[widgetPosition]);
          newDashboardWidget.widget = updatedWidget;
          this.dashboardWidgets.splice(widgetPosition, 1, newDashboardWidget);
          this.widgetsUpdated.next(this.dashboardWidgets);
        }
      });
  }

  private getWidgetForApiUpdate(existingWidget: Widget): Widget {
    const widgetForApi = Object.assign(new Widget(), existingWidget);
    widgetForApi.data = JSON.stringify(existingWidget.data);
    return widgetForApi;
  }

  public getItemizedWidgetDataCount(widget: Widget): Observable<number> {
    return this.backendPaginationService.getCountWithParameters<Widget>(Widget, this.getWidgetForApiUpdate(widget), false);
  }

  public getItemizedWidgetData(widget: Widget, first: number, quantity: number): Observable<any> {
    return this.backendPaginationService.paginateSerializedData(this.getWidgetForApiUpdate(widget), widget.apiRoute, first, quantity, false);
  }

  public createDashbaordWidget(widgetDataType: WidgetDataType): void {
    this.backendService.createDashboardWidget(this.dashboardId, widgetDataType).subscribe(result => {
      if (result) {
        this.dashboardWidgets.push(result);
        this.widgetsUpdated.next(this.dashboardWidgets);
      }
    });
  }

  public deleteWidget(widgetiId: string): void {
    this.backendService.delete<Widget>(Widget, widgetiId).subscribe(() => {
      const widgetPosition = this.dashboardWidgets.findIndex(dw => dw.widgetId === widgetiId);
      this.dashboardWidgets.splice(widgetPosition, 1);
      this.widgetsUpdated.next(this.dashboardWidgets);
    });
  }

  public updateWidgetPosition(widgetId: string, targetWidgetId: string): void {
    const dashboardWidget = this.dashboardWidgets.find(w => w.dashboardWidgetId === widgetId);
    const targetDashboardWidget = this.dashboardWidgets.find(w => w.dashboardWidgetId === targetWidgetId);

    if (!dashboardWidget || !targetDashboardWidget) {
      return;
    }

    const oldPosition = dashboardWidget.dashboardPosition;
    const newPosition = targetDashboardWidget.dashboardPosition;

    this.dashboardWidgets.forEach(widget => {
      if (widget.dashboardWidgetId === dashboardWidget.dashboardWidgetId) {
        widget.dashboardPosition = newPosition;
      } else if (widget.dashboardPosition > oldPosition && widget.dashboardPosition <= newPosition) {
        widget.dashboardPosition--;
      } else if (widget.dashboardPosition < oldPosition && widget.dashboardPosition >= newPosition) {
        widget.dashboardPosition++;
      }
    });

    this.dashboardWidgets.sort((a, b) => a.dashboardPosition - b.dashboardPosition);
    this.widgetsUpdated.next(this.dashboardWidgets);
  }

  public getWidgetsSettings(dashboardWidgets: DashboardWidget[]): WidgetSettings[] {
    const widgetsSettings: WidgetSettings[] = [];
    dashboardWidgets.forEach(e => {
      const { widget } = e;
      const widgetSettings = new WidgetSettings(
        e.widgetId,
        this.getWidgetColour(widget.widgetDataType),
        this.getDrillDownRoute(widget.widgetDataType, widget.orgTreeNodeId),
        this.getDrillDownFilters(widget.widgetDataType),
      );
      widgetsSettings.push(widgetSettings);
    });
    return widgetsSettings;
  }

  private getWidgetColour(widgetDataType: WidgetDataType): string {
    if (widgetDataType === WidgetDataType.OverdueMitigations || widgetDataType === WidgetDataType.HighRiskItems) {
      return this.brandDanger;
    } else {
      return this.brandInfo;
    }
  }

  private getDrillDownRoute(widgetDataType: WidgetDataType, orgTreeNodeId: string): string {
    if (widgetDataType === WidgetDataType.HighRiskItems || widgetDataType === WidgetDataType.AssetCount) {
      if (orgTreeNodeId) {
        return this.hierarchyDrilldownService.drillDownRoute + '/' + orgTreeNodeId;
      }
      return this.hierarchyDrilldownService.drillDownRoute;
    } else {
      return null;
    }
  }

  private getDrillDownFilters(widgetDataType: WidgetDataType): Map<string, any> {
    if (widgetDataType === WidgetDataType.HighRiskItems) {
      return this.highRiskItemFilters;
    } else if (widgetDataType === WidgetDataType.AssetCount) {
      return this.itemsPerAssetCountFilters;
    } else {
      return null;
    }
  }

  public checkUserHasPermissionsForWidget(type: WidgetDataType): boolean {
    const requiredPermissions = Widget.getRequiredPermissionsForWidget(type);
    return requiredPermissions.some(permission => this.permissionService.checkUserHasPermissions(permission));
  }
}
